import {
  ORG_STATUS_LABEL,
  SUBSCRIPTION_STATUS_LABEL,
  TICKET_PRIORITY_LABEL,
  TICKET_STATUS_LABEL,
  type OrgStatus,
  type PlatformRole,
  type SubscriptionAction,
  type SubscriptionStatus,
  type UsageItem,
} from '@brechoflow/contracts';
import { formatCurrency } from '@/lib/format';

export type Tone = 'neutral' | 'primary' | 'sand' | 'success' | 'warning' | 'danger';

export const ORG_STATUS_TONE: Record<OrgStatus, Tone> = { active: 'success', suspended: 'danger', pending_deletion: 'warning' };
export const SUB_STATUS_TONE: Record<SubscriptionStatus, Tone> = {
  trialing: 'primary',
  active: 'success',
  past_due: 'warning',
  grace: 'warning',
  suspended: 'danger',
  canceled: 'neutral',
};
export const orgStatusLabel = (s: OrgStatus) => ORG_STATUS_LABEL[s] ?? s;
export const subStatusLabel = (s: SubscriptionStatus) => SUBSCRIPTION_STATUS_LABEL[s] ?? s;

export const ACTION_LABEL: Record<SubscriptionAction, string> = {
  upgrade: 'Upgrade de plano',
  downgrade: 'Downgrade de plano',
  cancel: 'Cancelamento',
  reactivate: 'Reativação',
  trial_start: 'Início do teste',
  trial_convert: 'Teste convertido',
  trial_extend: 'Teste estendido',
  status_change: 'Mudança de situação',
};

export const EVENT_STATUS_LABEL: Record<string, string> = { pending: 'Pendente', processing: 'Processando', processed: 'Processado', failed: 'Falhou', ignored: 'Ignorado' };
export const EVENT_STATUS_TONE: Record<string, Tone> = { pending: 'neutral', processing: 'primary', processed: 'success', failed: 'danger', ignored: 'neutral' };
export const INVOICE_STATUS_LABEL: Record<string, string> = { pending: 'Pendente', confirmed: 'Confirmada', received: 'Recebida', overdue: 'Vencida', refunded: 'Estornada', canceled: 'Cancelada' };
export const INVOICE_STATUS_TONE: Record<string, Tone> = { pending: 'neutral', confirmed: 'primary', received: 'success', overdue: 'danger', refunded: 'warning', canceled: 'neutral' };

export const TICKET_STATUS_TONE: Record<string, Tone> = { open: 'primary', pending: 'warning', resolved: 'success', closed: 'neutral' };
export const TICKET_PRIORITY_TONE: Record<string, Tone> = { low: 'neutral', normal: 'neutral', high: 'warning', urgent: 'danger' };
export const ticketStatusLabel = (s: string) => (TICKET_STATUS_LABEL as Record<string, string>)[s] ?? s;
export const ticketPriorityLabel = (s: string) => (TICKET_PRIORITY_LABEL as Record<string, string>)[s] ?? s;

export const INTERVAL_LABEL = { month: 'mês', year: 'ano' } as const;

/** Preço do plano com o intervalo, ex.: "R$ 99,00 / mês". Valores sempre vêm do cadastro do operador. */
export const formatPlanPrice = (cents: number, interval: 'month' | 'year') => `${formatCurrency(cents)} / ${INTERVAL_LABEL[interval]}`;

/** Custo de IA vem em micro-reais (1 real = 1.000.000). */
export const formatMicros = (micros: number) => {
  const cents = micros / 10_000;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: cents < 1 && cents > 0 ? 4 : 2 }).format(cents / 100);
};

export const formatInt = (n: number) => new Intl.NumberFormat('pt-BR').format(n);

/** Taxa: aceita fração (0..1) ou percentual já multiplicado (>1). null = sem base de cálculo. */
export function formatRate(v: number | null | undefined): string {
  if (v == null) return 'Sem base';
  const pct = v <= 1 ? v * 100 : v;
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(pct)}%`;
}

/** Percentual de uso de um limite (0..100); ilimitado = null. */
export function usagePercent(u: Pick<UsageItem, 'used' | 'limit'>): number | null {
  if (u.limit == null) return null;
  if (u.limit === 0) return u.used > 0 ? 100 : 0;
  return Math.min(100, Math.round((u.used / u.limit) * 100));
}

export const formatLimit = (limit: number | null) => (limit == null ? 'Ilimitado' : formatInt(limit));

/** Mês "AAAA-MM" -> "outubro de 2026". */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(new Date(y, m - 1, 1));
}

export function recentMonths(n = 12, now = new Date()): string[] {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
}

// ---------- papéis ----------
type R = readonly PlatformRole[];
const ALL: R = ['owner', 'support', 'finance', 'readonly'];

export interface PlatformNavEntry {
  to: string;
  label: string;
  roles: R;
}

/** Menu da plataforma; cada item aparece só para os papéis listados. */
export const PLATFORM_NAV: PlatformNavEntry[] = [
  { to: '/plataforma', label: 'Visão geral', roles: ALL },
  { to: '/plataforma/organizacoes', label: 'Organizações', roles: ALL },
  { to: '/plataforma/usuarios', label: 'Usuários', roles: ALL },
  { to: '/plataforma/planos', label: 'Planos', roles: ['owner', 'finance', 'readonly'] },
  { to: '/plataforma/assinaturas', label: 'Assinaturas', roles: ALL },
  { to: '/plataforma/cobranca', label: 'Cobrança', roles: ['owner', 'finance', 'readonly'] },
  { to: '/plataforma/uso', label: 'Uso e IA', roles: ['owner', 'finance', 'readonly'] },
  { to: '/plataforma/suporte', label: 'Suporte', roles: ['owner', 'support', 'readonly'] },
  { to: '/plataforma/metricas', label: 'Métricas', roles: ['owner', 'finance', 'readonly'] },
  { to: '/plataforma/auditoria', label: 'Auditoria', roles: ['owner', 'readonly'] },
  { to: '/plataforma/servicos', label: 'Serviços', roles: ALL },
  { to: '/plataforma/configuracoes', label: 'Configurações', roles: ['owner', 'readonly'] },
];

export const navFor = (role: PlatformRole | null) => PLATFORM_NAV.filter((i) => role && i.roles.includes(role));
export const roleOf = (to: string) => PLATFORM_NAV.find((i) => i.to === to)?.roles ?? ALL;

/** O que cada papel pode alterar (o servidor valida de novo; aqui só se escondem botões). */
export const can = {
  suspendOrg: (r: PlatformRole | null) => r === 'owner' || r === 'support',
  manageSubscription: (r: PlatformRole | null) => r === 'owner' || r === 'support' || r === 'finance',
  deleteOrg: (r: PlatformRole | null) => r === 'owner',
  impersonate: (r: PlatformRole | null) => r === 'owner' || r === 'support',
  manageUsers: (r: PlatformRole | null) => r === 'owner' || r === 'support',
  createOrg: (r: PlatformRole | null) => r === 'owner' || r === 'support',
  editPlans: (r: PlatformRole | null) => r === 'owner' || r === 'finance',
  reprocess: (r: PlatformRole | null) => r === 'owner' || r === 'finance',
  answerTickets: (r: PlatformRole | null) => r === 'owner' || r === 'support',
  editSettings: (r: PlatformRole | null) => r === 'owner',
};
